import type { DebugConnectionHealthProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DebugTab/DebugRecord/DebugConnectionHealth/index.types';
import type {
  DevToolsTone,
} from 'Modules/softphone/components/SoftphoneDevTools/index.types';
import {
  CARD_TITLE,
  SURFACE,
  TONE,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import React from 'react';
import { cn } from '~/lib/utils';

const HEALTHY_STATES = ['connected', 'completed', 'stable', 'new'];
const BROKEN_STATES = ['failed', 'closed'];

const toneForState = (state: string | null): DevToolsTone => {
  if (!state) {
    return 'muted';
  }

  if (HEALTHY_STATES.includes(state)) {
    return 'ok';
  }

  return BROKEN_STATES.includes(state) ? 'bad' : 'warn';
};

/**
 * Zero candidates means no media path was ever negotiated on that side. A
 * single remote candidate is not broken, but it is the shape of a call that
 * later turns out to have had one-way audio, so it is worth a second look.
 */
const toneForCandidates = (count: number): DevToolsTone => {
  if (count === 0) {
    return 'bad';
  }

  return count === 1 ? 'warn' : 'ok';
};

/**
 * DebugConnectionHealth - The peer connection's final state machines, plus the ICE candidate counts that explain most one-way-audio reports.
 *
 * Each reading is tinted by whether it is good news.
 */
export const DebugConnectionHealth: React.FC<DebugConnectionHealthProps> = ({
  info,
}) => {
  const localCount = info.iceCandidates?.local.length ?? 0;
  const remoteCount = info.iceCandidates?.remote.length ?? 0;

  const readings: { label: string; value: string; tone: DevToolsTone }[] = [
    {
      label: 'ICE',
      value: info.iceConnectionState ?? 'unknown',
      tone: toneForState(info.iceConnectionState),
    },
    {
      label: 'Connection',
      value: info.connectionState ?? 'unknown',
      tone: toneForState(info.connectionState),
    },
    {
      label: 'Signaling',
      value: info.signalingState ?? 'unknown',
      tone: toneForState(info.signalingState),
    },
    {
      label: 'Local candidates',
      value: String(localCount),
      tone: toneForCandidates(localCount),
    },
    {
      label: 'Remote candidates',
      value: String(remoteCount),
      tone: toneForCandidates(remoteCount),
    },
  ];

  return (
    <div className={SURFACE.card}>
      <div className={cn(SURFACE.cardHeader, CARD_TITLE)}>Connection health</div>
      <div className="grid grid-cols-2 gap-1.5 p-3">
        {readings.map(({ label, value, tone }) => (
          <div
            key={label}
            className={cn(
              'flex items-center gap-2 px-[9px] py-[7px]',
              TONE[tone].inset
            )}
          >
            <span
              className={cn(
                'h-1.5 w-1.5 flex-shrink-0 rounded-full',
                TONE[tone].dot
              )}
            />
            <span className="flex-1 truncate text-[11px] text-zinc-400">
              {label}
            </span>
            <span
              className={cn(
                'font-sp-mono text-[11px] font-medium',
                TONE[tone].text
              )}
            >
              {value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
